import React, { useId } from 'react';
import {
  TIERS, FROM_PRICE, ENTRY_PRICE_NOTE, CUSTOM_QUOTE,
  AI_ACT, AI_ACT_FORM_URL, DSA, DSA_PRICE_SECOND, VAT_EXCLUDED, usd,
} from '@/lib/pricing';
import { SERVICES, PRICING_PAGE } from './services.js';

// <PricingSummary plan="essential|quote" service="gdpr|ai-act|dsa|data-act" />
// The "What it costs" card in a Resources article: a navy offer panel and a
// white "What's included" panel side by side from 720px, with the Startup
// Program in a tinted footer strip (GDPR only). Prices, billing term, revenue
// limit and the inclusions come from src/lib/pricing.js (the data
// /pricing renders), so an article never retypes a plan.

const STARTUP_HREF = '/pricing/#startup';
const QUOTE_HREF = '/contact/';
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const planFor = (plan, service) => {
  if (plan === 'essential') {
    const t = TIERS[0];
    return {
      label: `GDPR ${t.name} plan`,
      price: { amount: `$${FROM_PRICE}`, per: 'per month' },
      terms: [`${cap(ENTRY_PRICE_NOTE)}. ${VAT_EXCLUDED}.`, `For companies under ${t.revenueLimit} in revenue.`],
      primary: { href: PRICING_PAGE.href, label: 'See pricing' },
      proof: 'Set up in about five minutes. Companies and individuals can both sign up.',
      items: t.summary,
      all: { href: PRICING_PAGE.href, label: 'See everything in the plan' },
      footer: true,
    };
  }
  // AI Act and DSA have published from-prices; the Data Act stays quoted.
  if (plan === 'quote' && service === 'ai-act') {
    return {
      label: SERVICES[service].name,
      price: { amount: usd(AI_ACT.from), per: `per ${AI_ACT.period}` },
      terms: [`${VAT_EXCLUDED}. For one high-risk AI system.`, 'Multiple systems, general-purpose AI models and larger organisations are quoted individually.'],
      primary: { href: AI_ACT_FORM_URL, label: 'Get a quote' },
      proof: 'Free scoping. We reply within one business day.',
      items: CUSTOM_QUOTE.summary[service],
      all: { href: SERVICES[service].href, label: 'See everything in the service' },
      footer: false,
    };
  }
  if (plan === 'quote' && service === 'dsa') {
    return {
      label: SERVICES[service].name,
      price: { amount: usd(DSA.monthly), per: 'per month, billed annually' },
      terms: [`${DSA_PRICE_SECOND}.`, `${VAT_EXCLUDED}.`],
      primary: { href: QUOTE_HREF, label: 'Request a quote' },
      proof: 'We reply within one business day.',
      items: CUSTOM_QUOTE.summary[service],
      all: { href: SERVICES[service].href, label: 'See everything in the service' },
      footer: false,
    };
  }
  if (plan === 'quote') {
    const basis = CUSTOM_QUOTE.basis[service];
    if (!basis) throw new Error(`PricingSummary: plan="quote" needs service="ai-act", "dsa" or "data-act", got "${service}"`);
    return {
      label: SERVICES[service].name,
      price: { quote: basis },
      terms: null,
      primary: { href: QUOTE_HREF, label: 'Request a quote' },
      proof: 'We reply within one business day.',
      items: CUSTOM_QUOTE.summary[service],
      all: { href: SERVICES[service].href, label: 'See everything in the service' },
      footer: false,
    };
  }
  throw new Error(`PricingSummary: unknown plan "${plan}" (use "essential" or "quote")`);
};

const Check = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
    <path d="M4 10.5l4 4 8-9" stroke="#003399" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const PricingSummary = ({ plan = 'essential', service = 'gdpr' }) => {
  const id = useId();
  const s = SERVICES[service];
  if (!s) throw new Error(`PricingSummary: unknown service "${service}"`);
  const p = planFor(plan, service);
  return (
    <section className="ps" aria-labelledby={`${id}-title`}>
      <div className="ps-offer">
        <p className="ps-plan" id={`${id}-title`}>{p.label}</p>
        {p.price.amount ? (
          <p className="ps-price"><span className="amount">{p.price.amount}</span><span className="per">{p.price.per}</span></p>
        ) : (
          <p className="ps-price"><span className="quote">{p.price.quote}</span></p>
        )}
        {p.terms && <p className="ps-terms">{p.terms[0]}<br />{p.terms[1]}</p>}
        <div className="ps-actions">
          <a className="ps-btn" href={p.primary.href}>{p.primary.label}</a>
          <a className="ps-link" href={s.href}>How the service works</a>
        </div>
        <p className="ps-proof">{p.proof}</p>
      </div>

      <div className="ps-included">
        <h3>What&apos;s included</h3>
        <ul>
          {p.items.slice(0, 4).map((item) => <li key={item}><Check /><span>{item}</span></li>)}
        </ul>
        <a className="ps-all" href={p.all.href}>{p.all.label}</a>
      </div>

      {p.footer && (
        <div className="ps-foot">
          Early-stage, or no company yet? <a href={STARTUP_HREF}>Ask about the Startup Program</a>
        </div>
      )}
    </section>
  );
};

export default PricingSummary;
